<div class="space-y-4">
  <div class="card">
    <div class="card-body">
      <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p class="card-title">DES Credits</p>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          [disabled]="!accountNumber || creating"
          (click)="openCreate()"
        >
          Add DES Credit Record
        </button>
      </div>

      @if (error) {
        <p class="text-error">{{ error }}</p>
      } @else if (loading && records.length === 0) {
        <div class="skeleton h-32 w-full"></div>
      } @else if (records.length === 0) {
        <p>No DES credit record found</p>
        @if (desApiKeyLink?.lookup_error) {
          <p class="mt-2 text-error">{{ desApiKeyLink?.lookup_error }}</p>
        } @else if (showMultipleLinks) {
          <p class="alert alert-warning mt-2">{{ multipleLinksWarning }}</p>
        } @else if (showNotLinked) {
          <p class="alert alert-warning mt-2">{{ notLinkedMessage }}</p>
        }
      } @else {
        @if (desApiKeyLink?.lookup_error) {
          <p class="mb-3 text-error">{{ desApiKeyLink?.lookup_error }}</p>
        } @else if (showMultipleLinks) {
          <p class="alert alert-warning mb-3">{{ multipleLinksWarning }}</p>
        } @else if (showNotLinked) {
          <div class="alert alert-warning mb-3">{{ notLinkedMessage }}</div>
        }
        <div class="overflow-x-auto rounded-lg border border-base-content/20">
          <table class="table table-zebra table-sm">
            <thead>
              <tr>
                <th>Credits</th>
                <th>Registration ID</th>
                <th>GUID</th>
                <th>Linked to API Key</th>
                <th>Expiration</th>
                <th>Last used</th>
                <th>Computer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (rec of records; track rec.registration_id) {
                <tr>
                  <td class="text-lg font-semibold">
                    {{ displayCredit(rec) }}
                  </td>
                  <td>{{ rec.registration_id }}</td>
                  <td class="font-mono text-xs">{{ rec.guid || '—' }}</td>
                  <td>{{ rec.linked_to_api_key ? 'Yes' : '—' }}</td>
                  <td>{{ rec.expiration || '—' }}</td>
                  <td>{{ rec.lastused || '—' }}</td>
                  <td>{{ rec.computername || '—' }}</td>
                  <td class="text-right">
                    <div class="flex justify-end gap-1">
                      <button
                        type="button"
                        class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-base-300/40 hover:text-base-content/80"
                        title="Download des.dat"
                        [disabled]="!accountNumber || downloadingId === rec.registration_id"
                        (click)="downloadDat(rec)"
                      >
                        <app-file-code-icon class="h-5 w-5"></app-file-code-icon>
                      </button>
                      <button
                        type="button"
                        class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-base-300/40 hover:text-base-content/80"
                        title="Purchase credits"
                        [disabled]="!tenantId"
                        (click)="buyCredits(rec)"
                      >
                        <app-cart-icon class="h-5 w-5"></app-cart-icon>
                      </button>
                      <button
                        type="button"
                        class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-base-300/40 hover:text-base-content/80"
                        title="Edit credits"
                        (click)="openEdit(rec)"
                      >
                        <app-edit-icon></app-edit-icon>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>

  <div class="card">
    <div class="card-body">
      <p class="card-title mb-4">Purchase History</p>

      @if (errorPurchases) {
        <p class="text-error">{{ errorPurchases }}</p>
      } @else if (loadingPurchases && purchases.length === 0) {
        <div class="skeleton h-32 w-full"></div>
      } @else if (purchases.length === 0) {
        <p>No purchases</p>
      } @else {
        <div class="overflow-x-auto rounded-lg border border-base-content/20">
          <table class="table table-zebra table-sm static">
            <thead>
              <tr>
                <td>Completed At</td>
                <td>Total Amount</td>
                <td>Credits</td>
                <td>Created Date</td>
              </tr>
            </thead>
            <tbody>
              @for (purchase of purchases; track purchase.id) {
                <tr class="cursor-pointer" (click)="browseToReceipt(purchase)">
                  <td>
                    {{ purchase.completed_at | date: 'MM/dd/yyyy, hh:mmaa' }}
                  </td>
                  <td>{{ purchase.total_amount | currency }}</td>
                  <td>{{ purchase.credits }}</td>
                  <td>
                    {{ purchase.created_at | date: 'MM/dd/yyyy, hh:mmaa' }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>
</div>

<dialog #createDialog class="modal">
  <div class="modal-box max-w-md p-8">
    <h3 class="text-lg font-bold">Add DES Credit Record</h3>
    <p class="mt-1 text-sm opacity-70">
      Creates a new DES credit record for this tenant.
    </p>
    <label class="form-control mt-4 w-full">
      <div class="label">
        <span class="label-text">Credits</span>
      </div>
      <input
        type="number"
        min="0"
        step="1"
        class="input input-bordered w-full"
        [(ngModel)]="createCreditInput"
      />
    </label>
    @if (createError) {
      <p class="mt-3 text-sm text-error">{{ createError }}</p>
    }
    <div class="mt-6 flex justify-end gap-2">
      <button
        type="button"
        class="btn btn-neutral"
        [disabled]="creating"
        (click)="closeCreate()"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary"
        [disabled]="!canCreate"
        (click)="create()"
      >
        @if (creating) {
          <span class="loading loading-spinner loading-sm"></span>
        }
        Create
      </button>
    </div>
  </div>
  <form method="dialog" class="modal-backdrop">
    <button type="submit" (click)="closeCreate()">close</button>
  </form>
</dialog>

<dialog #editDialog class="modal">
  <div class="modal-box max-w-md">
    <h3 class="mb-4 text-lg font-bold">Edit DES credits</h3>
    @if (editingRecord) {
      <p class="mb-2 text-sm text-base-content/70">
        Registration ID {{ editingRecord.registration_id }}
      </p>
      <label class="form-control w-full">
        <div class="label">
          <span class="label-text">Credits</span>
        </div>
        <input
          type="number"
          min="0"
          step="1"
          class="input input-bordered w-full"
          [(ngModel)]="creditInput"
        />
      </label>
      @if (saveError) {
        <p class="mt-3 text-error">{{ saveError }}</p>
      }
    }
    <div class="modal-action flex-wrap items-center justify-between gap-2">
      <div class="flex gap-2">
        @if (showLinkButton) {
          <button
            type="button"
            class="btn btn-outline"
            [disabled]="saving || linking"
            (click)="openLinkConfirm('link')"
          >
            Link to API Key
          </button>
        }
        @if (showUnlinkButton) {
          <button
            type="button"
            class="btn btn-outline btn-error"
            [disabled]="saving || linking"
            (click)="openLinkConfirm('unlink')"
          >
            Unlink from API Key
          </button>
        }
      </div>
      <div class="flex gap-2">
        <button type="button" class="btn btn-outline" (click)="closeEdit()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          [disabled]="!canSave"
          (click)="save()"
        >
          @if (saving) {
            <span class="loading loading-spinner loading-sm"></span>
          }
          Save
        </button>
      </div>
    </div>
  </div>
  <form method="dialog" class="modal-backdrop">
    <button type="submit" (click)="closeEdit()">close</button>
  </form>
</dialog>

<dialog #linkConfirmDialog class="modal">
  <div class="modal-box max-w-lg p-8">
    <h3 class="text-lg font-bold">{{ linkConfirmTitle }}</h3>
    <p class="mt-4 text-sm">{{ linkConfirmBody }}</p>
    @if (linkError) {
      <p class="mt-4 text-error">{{ linkError }}</p>
    }
    <div class="mt-6 flex justify-end gap-2">
      <button
        type="button"
        class="btn btn-neutral"
        [disabled]="linking"
        (click)="closeLinkConfirm()"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn"
        [ngClass]="pendingLinkAction === 'unlink' ? 'btn-error' : 'btn-primary'"
        [disabled]="linking || !pendingLinkAction"
        (click)="confirmLinkAction()"
      >
        @if (linking) {
          <span class="loading loading-spinner loading-sm"></span>
        }
        @if (pendingLinkAction === 'unlink') {
          Unlink
        } @else {
          Link
        }
      </button>
    </div>
  </div>
  <form method="dialog" class="modal-backdrop">
    <button type="submit" (click)="closeLinkConfirm()">close</button>
  </form>
</dialog>
